import { getBaseDomain } from '../utils/domainConfig';

// Get user data from localStorage
const getUserData = function() {
  try {
    const userData = localStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing userData from localStorage:', error);
    return null;
  }
};

// Get current user's hierarchy level
const getCurrentUserLevel = function() {
  const userData = getUserData();
  if (userData?.userType === 'Finance') return 1;
  return userData?.hierarchyLevel || 0;
};

// Check if the logged-in user has Finance userType
const isFinanceUser = function() {
  const userData = getUserData();
  return userData?.userType === 'Finance';
};

// User level hierarchy (lower number = higher authority)
export const USER_LEVELS = {
  ADMIN: 1,
  SUPERADMIN: 2,
  MINIADMIN: 3,
  MASTER: 4,
  SUPER: 5,
  AGENT: 6,
  CLIENT: 7,
};

// Reverse mapping for display names
export const LEVEL_NAMES = {
  1: 'Admin',
  2: 'SuperAdmin',
  3: 'MiniAdmin',
  4: 'Master',
  5: 'Super',
  6: 'Agent',
  7: 'Client',
};

// Get levels that a user can manage (all levels below them - higher numbers)
export const getManageableLevels = function(userLevel) {
  const levels = [];
  for (let i = userLevel + 1; i <= 7; i++) {
    levels.push(i);
  }
  return levels;
};

// Check if user can see a specific menu item based on required level
export const canAccessMenu = function(userLevel, requiredLevel) {
  return userLevel <= requiredLevel;
};

// Export functions to access user data
export { getUserData, getCurrentUserLevel };

// Get reports children based on user hierarchy level
const getReportsChildren = function() {
  const userLevel = getCurrentUserLevel();
  const allReportsMenus = [
    {
      key: 'login-report',
      label: 'Login Report',
      path: '/reports/login-report',
    },
    {
      key: 'company-report',
      label: 'Company Report',
      path: '/reports/company-report',
    },
  ];

  // Filter based on user level
  return allReportsMenus.filter(function(menu) {
    // If menu has a specific level requirement, only show for that level
    if (menu.level !== undefined) {
      return userLevel === menu.level;
    }
    // Otherwise show to all
    return true;
  });
};

// Get admin details children based on user hierarchy level
const getAdminDetailsChildren = function() {
  const userLevel = getCurrentUserLevel();
  const allAdminMenus = [
    {
      key: 'super-admin-list',
      label: 'SuperAdmin',
      path: '/admin-details/super-admin',
      level: 2
    },
    {
      key: 'mini-admin-list',
      label: 'MiniAdmin',
      path: '/admin-details/mini-admin',
      level: 3
    },
    {
      key: 'master-list',
      label: 'Master',
      path: '/admin-details/master',
      level: 4
    },
    {
      key: 'super-list',
      label: 'Super',
      path: '/admin-details/super',
      level: 5
    },
    {
      key: 'agent-list',
      label: 'Agent',
      path: '/admin-details/agent',
      level: 6
    },
    {
      key: 'client-list',
      label: 'Client',
      path: '/admin-details/client',
      level: 7
    },
    {
      key: 'commission-report',
      label: 'Commission Len Den',
      path: '/ledger/comm-lenden',
      level: 7,
      agentOnly: true,
    }
  ];

  // Show menus based on hierarchy level - users can only see lower hierarchy levels
  if (userLevel === 1) {
    return allAdminMenus.filter(menu => !menu.agentOnly);
  } else if (userLevel === 2) {
    return allAdminMenus.filter(menu => menu.level >= 3 && !menu.agentOnly);
  } else if (userLevel === 3) {
    return allAdminMenus.filter(menu => menu.level >= 4 && !menu.agentOnly);
  } else if (userLevel === 4) {
    return allAdminMenus.filter(menu => menu.level >= 5 && !menu.agentOnly);
  } else if (userLevel === 5) {
    return allAdminMenus.filter(menu => menu.level >= 6 && !menu.agentOnly);
  } else if (userLevel === 6) {
    // Agent - Show Client + Commission Len Den (agentOnly items with level 7)
    return allAdminMenus.filter(menu => menu.level >= 7);
  } else {
    return [];
  }
};

// Get ledger children based on user hierarchy level
const getLedgerChildren = function() {
  const userLevel = getCurrentUserLevel();
  const allLedgerMenus = [
    {
      key: 'total-profit',
      label: 'Total Profit',
      path: '/ledger/total-profit',
      level: 1  // Only for Admin level
    },
    {
      key: 'my-ledger',
      label: 'My Ledger',
      path: '/ledger/my-ledger',
      level: 0
    },
    {
      key: 'super-admin-ledger',
      label: 'Super Admin Ledger',
      path: '/ledger/super-admin-ledger',
      level: 2
    },
    {
      key: 'mini-ledger',
      label: 'Mini Ledger',
      path: '/ledger/mini-ledger',
      level: 3
    },
    {
      key: 'master-ledger',
      label: 'Master Ledger',
      path: '/ledger/master-ledger',
      level: 4
    },
    {
      key: 'super-ledger',
      label: 'Super Ledger',
      path: '/ledger/super-ledger',
      level: 5
    },
    {
      key: 'agent-ledger',
      label: 'Agent Ledger',
      path: '/ledger/agent-ledger',
      level: 6
    },
    {
      key: 'client-ledger',
      label: 'Client Ledger',
      path: '/ledger/client-ledger',
      level: 7
    }
  ];

  // Show Total Profit only for Admin level (level 1)
  const baseMenus = allLedgerMenus.filter(menu => {
    if (menu.level === 1) {
      return userLevel === 1; // Total Profit only for Admin
    }
    return menu.level === 0; // My Ledger for all
  });
  
  // Show ledger menus based on hierarchy level - users can only see lower hierarchy levels
  let downlineMenus = [];
  if (userLevel === 1) {
    // Admin (level 1) - Show all lower levels (SuperAdmin to Client)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 2);
  } else if (userLevel === 2) {
    // SuperAdmin (level 2) - Show MiniAdmin to Client (level 3-7)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 3);
  } else if (userLevel === 3) {
    // MiniAdmin (level 3) - Show Master to Client (level 4-7)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 4);
  } else if (userLevel === 4) {
    // Master (level 4) - Show Super to Client (level 5-7)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 5);
  } else if (userLevel === 5) {
    // Super (level 5) - Show Agent to Client (level 6-7)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 6);
  } else if (userLevel === 6) {
    // Agent (level 6) - Show only Client (level 7)
    downlineMenus = allLedgerMenus.filter(menu => menu.level >= 7);
  }
  
  return [...baseMenus, ...downlineMenus];
};

// Get dead client children based on user hierarchy level
const getDeadClientChildren = function() {
  const userLevel = getCurrentUserLevel();
  const allDeadClientMenus = [
    {
      key: 'dead-super-admin',
      label: 'Dead Super Admin',
      path: '/settings/dead-client/super-admin',
      level: 2
    },
    {
      key: 'dead-mini-admin',
      label: 'Dead Mini Admin',
      path: '/settings/dead-client/mini-admin',
      level: 3
    },
    {
      key: 'dead-master-agent',
      label: 'Dead Master Agent',
      path: '/settings/dead-client/master-agent',
      level: 4
    },
    {
      key: 'dead-super-agent',
      label: 'Dead Super Agent',
      path: '/settings/dead-client/super-agent',
      level: 5
    },
    {
      key: 'dead-agent',
      label: 'Dead Agent',
      path: '/settings/dead-client/agent',
      level: 6
    },
    {
      key: 'dead-client',
      label: 'Dead Client',
      path: '/settings/dead-client/client',
      level: 7
    }
  ];

  // Show dead client menus based on hierarchy level
  let result = [];
  if (userLevel === 1) {
    // Admin (level 1) - Show all (SuperAdmin to Client)
    result = allDeadClientMenus;
  } else if (userLevel === 2) {
    // SuperAdmin (level 2) - Show MiniAdmin to Client (level 3-7)
    result = allDeadClientMenus.filter(menu => menu.level >= 3);
  } else if (userLevel === 3) {
    // MiniAdmin (level 3) - Show Master to Client (level 4-7)
    result = allDeadClientMenus.filter(menu => menu.level >= 4);
  } else if (userLevel === 4) {
    // Master (level 4) - Show Super to Client (level 5-7)
    result = allDeadClientMenus.filter(menu => menu.level >= 5);
  } else if (userLevel === 5) {
    // Super (level 5) - Show Agent to Client (level 6-7)
    result = allDeadClientMenus.filter(menu => menu.level >= 6);
  } else if (userLevel === 6) {
    // Agent (level 6) - Show only Client (level 7)
    result = allDeadClientMenus.filter(menu => menu.level >= 7);
  } else {
    // Client - show nothing
    result = [];
  }
  
  return result;
};

// Get cash transaction children based on user hierarchy level
const getCashTransactionChildren = function() {
  const userLevel = getCurrentUserLevel();
  const allCashTransactionMenus = [
    {
      key: 'entry-super-admin',
      label: 'Debit/Credit Entry(Super Admin)',
      path: '/cash-transaction/super-admin',
      level: 2
    },
    {
      key: 'entry-mini-admin',
      label: 'Debit/Credit Entry(Mini Ad)',
      path: '/cash-transaction/mini-admin',
      level: 3
    },
    {
      key: 'entry-master',
      label: 'Debit/Credit Entry(MA)',
      path: '/cash-transaction/master',
      level: 4
    },
    {
      key: 'entry-super-agent',
      label: 'Debit/Credit Entry(SA)',
      path: '/cash-transaction/super-agent',
      level: 5
    },
    {
      key: 'entry-agent',
      label: 'Debit/Credit Entry(A)',
      path: '/cash-transaction/agent',
      level: 6
    },
    {
      key: 'entry-client',
      label: 'Debit/Credit Entry(C)',
      path: '/cash-transaction/client',
      level: 7
    }
  ];

  // Show cash transaction menus based on hierarchy level
  let result = [];
  if (userLevel === 1) {
    // Admin (level 1) - Show all (SuperAdmin to Client)
    result = allCashTransactionMenus;
  } else if (userLevel === 2) {
    // SuperAdmin (level 2) - Show MiniAdmin to Client (level 3-7)
    result = allCashTransactionMenus.filter(menu => menu.level >= 3);
  } else if (userLevel === 3) {
    // MiniAdmin (level 3) - Show Master to Client (level 4-7)
    result = allCashTransactionMenus.filter(menu => menu.level >= 4);
  } else if (userLevel === 4) {
    // Master (level 4) - Show Super to Client (level 5-7)
    result = allCashTransactionMenus.filter(menu => menu.level >= 5);
  } else if (userLevel === 5) {
    // Super (level 5) - Show Agent to Client (level 6-7)
    result = allCashTransactionMenus.filter(menu => menu.level >= 6);
  } else if (userLevel === 6) {
    // Agent (level 6) - Show only Client (level 7)
    result = allCashTransactionMenus.filter(menu => menu.level >= 7);
  } else {
    // Client - show nothing
    result = [];
  }
  
  return result;
};

// Menu items with minLevel (minimum user level required to see this menu)
export const getMenuItems = function() {
  return [
    {
      key: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'dashboard',
      minLevel: 0,
    },
    {
      key: 'admin-details',
      label: 'Admin Details',
      icon: 'contacts',
      children: getAdminDetailsChildren(),
    },
  {
    key: 'sports-detail',
    label: 'Sports Detail',
    icon: 'navigation',
    children: [
      {
        key: 'cricket-detail',
        label: 'Inplay Match',
        path: '/sports-detail/cricket',
      },
      {
        key: 'completed-matches',
        label: 'Completed Match',
        path: '/sports-detail/completed-matches',
      },
      // {
      //   key: 'casino-detail',
      //   label: 'Casino Detail',
      //   path: '/sports-detail/casino',
      // },
      // {
      //   key: 'matka-detail',
      //   label: 'Matka Detail',
      //   path: '/sports-detail/matka',
      // },
    ],
  },
  {
    key: 'casino',
    label: 'Casino',
    icon: 'navigation',
    children: [
      {
        key: 'inplay-casino',
        label: 'Inplay Casino',
        path: '/casino/inplay-casino',
      },
      {
        key: 'completed-casino',
        label: 'Completed Casino',
        path: '/casino/completed-casino',
      },
    ],
  },
  {
    key: 'matka',
    label: 'Matka',
    icon: 'navigation',
    children: [
      {
        key: 'inplay-matka',
        label: 'Inplay Matka',
        path: '/matka/inplay',
      },
      {
        key: 'completed-matka',
        label: 'Completed Matka',
        path: '/matka/completed',
      },
    ],
  },
  {
    key: 'ludo',
    label: 'Virtual Casino',
    icon: 'navigation',
    children: [
      {
        key: 'inplay-ludo',
        label: 'Inplay',
        path: '/ludo/inplay',
      },
      {
        key: 'completed-ludo',
        label: 'Completed',
        path: '/ludo/completed',
      },
    ],
  },
  {
    key: 'ledger',
    label: 'Ledger',
    icon: 'data-entry',
    children: getLedgerChildren(),
  },
  {
    key: 'cash-transaction',
    label: 'Cash Transaction',
    icon: 'data-display',
    children: getCashTransactionChildren(),
  },
  {
    key: 'reports',
    label: 'Reports',
    icon: 'feedback',
    children: getReportsChildren(),
    minLevel: 0,
  },
 
  {
    key: 'settings',
    label: 'Setting',
    icon: 'feedback',
    children: [
      // {
      //   key: 'ur-bet-setting',
      //   label: 'ur-bet Setting',
      //   path: '/settings/ur-bet-setting',
      // },
      {
        key: 'setting',
        label: 'Setting',
        path: '/settings/setting',
        minLevel: USER_LEVELS.ADMIN,
      },
      // domain-wise client site theme — sirf sky99.co ke admin pe, sirf top Admin (sidebar level =
      // userTypeInfo; top Admin = 7, Set Message / Rollback bhi `userTypeInfo === 7`). minLevel 6 => sirf 7.
      ...(getBaseDomain() === 'sky99.co' ? [{
        key: 'site-theme',
        label: 'Site Theme',
        path: '/settings/site-theme',
        minLevel: 6,
      }] : []),
      // {
      //   key: 'matka-casino-setting',
      //   label: 'Matka and Casino',
      //   path: '/settings/matka',
      //   maxLevel: USER_LEVELS.ADMIN,
      // },
   
      // {
      //   key: 'superadmin-matka-lock',
      //   label: 'Super Admin Matka',
      //   path: '/settings/superadmin-matka',
      //   minLevel: USER_LEVELS.ADMIN,
      // },
    ],
    minLevel: 0,
  },
  {
    key: 'dead-client',
    label: 'Dead Client',
    icon: 'contacts',
    children: getDeadClientChildren(),
  },
  {
    key: 'old-data',
    label: 'Old Data',
    path: 'https://oldadmin.urb99.pro/signin',
    icon: 'attachment',
    external: true,
    minLevel: USER_LEVELS.ADMIN,
  },
  ...(isFinanceUser() ? [{
    key: 'finance',
    label: 'Finance',
    path: '/finance',
    icon: 'data-entry',
    minLevel: 0,
  }] : []),
];
};

// Maintain backward compatibility
export const menuItems = getMenuItems();

// Filter menu items based on user level
export const getFilteredMenuItems = function(userLevel) {
  const currentMenuItems = getMenuItems();
  return currentMenuItems.map(function(item) {
    // Check if user can see this menu item
    if (item.minLevel !== undefined && userLevel <= item.minLevel) {
      return null;
    }
    if (item.maxLevel !== undefined && userLevel > item.maxLevel) {
      return null;
    }

    // If item has children, filter them too
    if (item.children) {
      // Check if children is a function (dynamic children like getAdminDetailsChildren)
      const children = typeof item.children === 'function' ? item.children() : item.children;
      
      const filteredChildren = children.filter(function(child) {
        // If child has minLevel, check if user can access
        if (child.minLevel !== undefined) {
          return userLevel > child.minLevel;
        }
        if (child.maxLevel !== undefined) {
          return userLevel <= child.maxLevel;
        }
        // If child has level property (for hierarchical filtering), it's already filtered
        // Just return true
        return true;
      });

      // If no children left after filtering, hide the parent
      if (filteredChildren.length === 0) {
        return null;
      }

      return {
        ...item,
        children: filteredChildren,
      };
    }

    return item;
  }).filter(function(item) {
    return item !== null;
  });
};
